// ----------------------------------------------------------------------
// <copyright file="Izenda.TreeActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

!(function() {
  var React = require('react');
  var compose = require('recompose').compose;
  var withHandlers = require('recompose').withHandlers;
  var observable = require('mobx').observable;
  const {
    curry,
    toUpper,
    filter,
    map,
    flow,
    isNil,
    isEmpty,
    isPlainObject,
    some,
    size,
    forEach,
    noop,
    isEqual,
    find,
    get,
    split
  } = require('lodash/fp');
  var TreeActions = compose(
    withHandlers({
      buildTree: curry(buildTree),
      getKey: curry(getKey),
      onChangeAll: curry(onChangeAll),
      onChangeCategory: curry(onChangeCategory),
      onChangeSubCategory: curry(onChangeSubCategory),
      onChangeLeafNode: curry(onChangeLeafNode)
    })
  );

  /**
   * Build report tree
   * @param {Object} props
   * @param {Object} data
   * @returns {Object} tree
   */
  function buildTree(props, data, filterText) {
    var leafKey = props.leafKey;
    filterText = toUpper(filterText);

    var root = function() {
      var childNodes = flow(
        map(getCategory),
        filter(function(c) {
          return !isNil(c);
        })
      )(data.categories);
      if (isEmpty(childNodes) && toUpper(data.name).indexOf(filterText) === -1) {
        return null;
      }

      data.indeterminate = checkIndeterminate(childNodes);
      data.checked = data.indeterminate ? true : getAllChecked(props.dataPrev, data, data.categories);
      props.resetPrevData();

      return {
        nodeType: data.name,
        source: data,
        getChildNodes: function() {
          return childNodes;
        }
      };
    };

    var getCategory = function(category) {
      var childNodes;
      if (category.subCategories.length > 0) {
        childNodes = getLeafNodeForCategoryAndSubCategory(category.subCategories);
        childNodes = filter(function(c) {
          return !isNil(c);
        }, childNodes);
      }

      if (isEmpty(childNodes)) {
        var name = isNil(category.name) ? 'UNCATEGORIZED' : category.name;
        if (toUpper(name).indexOf(filterText) === -1) {
          return null;
        }
      }

      category.indeterminate = checkIndeterminate(childNodes);
      category.checked = category.indeterminate
        ? true
        : getCategoryChecked(leafKey, props.dataPrev, category, category.subCategories);

      return {
        nodeType: 'Category',
        source: category,
        getChildNodes: function() {
          return childNodes;
        }
      };
    };

    var getLeafNodeForCategoryAndSubCategory = function(subCategories) {
      var data = [];
      forEach(function(subCategory) {
        if (isNil(subCategory.id)) {
          data = data.concat(getLeafNodes(subCategory[leafKey]));
        } else {
          data = data.concat(getSubCategory(subCategory));
        }
      }, subCategories);

      return data;
    };

    var getSubCategory = function(subCategory) {
      var childNodes = getLeafNodes(subCategory[leafKey]);
      if (isEmpty(childNodes)) {
        if (isNil(subCategory.name)) {
          return null;
        } else {
          if (toUpper(subCategory.name).indexOf(filterText) === -1) {
            return null;
          }
        }
      }

      subCategory.indeterminate = checkIndeterminate(childNodes);
      subCategory.checked = subCategory.indeterminate
        ? true
        : getSubCategoryChecked(props.dataPrev, subCategory, subCategory[leafKey]);

      return {
        nodeType: 'SubCategory',
        source: subCategory,
        getChildNodes: function() {
          return childNodes;
        }
      };
    };

    var getLeafNodes = function(leafs) {
      return map(
        getLeafNode,
        filter(function(leaf) {
          return toUpper(leaf.name).indexOf(filterText) > -1;
        }, leafs)
      );
    };

    var getLeafNode = function(leaf) {
      leaf.checked = getLeafNodeChecked(props.dataPrev, leaf);

      return {
        nodeType: leafKey,
        source: leaf,
        getChildNodes: noop
      };
    };

    buildMapProps(leafKey);
    return isPlainObject(data) ? [root()] : null;
  }

  /**
   * Build report tree
   * @param {Object} props
   * @param {Object} node
   * @returns {String} id
   */
  function getKey(props, node) {
    return node.source.id;
  }

  /**
   * Check indeterminate
   * @param {Object} data
   * @returns {Boolean} has indeterminate
   */
  function checkIndeterminate(data) {
    if (!isNil(data) && !isNil(data[0])) {
      var isExistOneIndeterminate = flow(
        map('source'),
        map(function(d) {
          return { indeterminate: d.indeterminate };
        }),
        some({ indeterminate: true })
      )(data);
      if (isExistOneIndeterminate) return true;

      var countChecked = flow(map('source'), filter({ checked: true }), size)(data);
      return countChecked !== 0 && data.length !== countChecked;
    }
  }

  /**
   * Get checked for all checkbox
   * @param {Object} data previous
   * @param {Object} source
   * @param {Object} category list
   * @returns {Boolean} checked
   */
  function getAllChecked(dataPrev, source, categories) {
    var id = source.id;
    var checked = source.checked;

    if (!isEmpty(dataPrev)) {
      checked = !some({ checked: false }, categories);
    }
    return checked;
  }

  /**
   * Get checked for category checkbox
   * @param {String} leaf key
   * @param {Object} data previous
   * @param {Object} source
   * @param {Object} category list
   * @returns {Boolean} checked
   */
  function getCategoryChecked(leafKey, dataPrev, source, subCategories) {
    var id = source.id;
    var checked = source.checked;

    if (!isEmpty(dataPrev)) {
      forEach(function(subCategory) {
        if (isNil(subCategory.id)) checked = true;
        else {
          if (!subCategory.checked) {
            checked = false;
            return false;
          }
        }

        forEach(function(leaf) {
          if (!leaf.checked) {
            checked = false;
            return false;
          }
        }, subCategory[leafKey]);

        if (!checked) return false;
      }, subCategories);
    }
    return checked;
  }

  /**
   * Get checked for sub-category checkbox
   * @param {Object} data previous
   * @param {Object} source
   * @param {Object} category list
   * @returns {Boolean} checked
   */
  function getSubCategoryChecked(dataPrev, source, leafs) {
    var id = source.id;
    var checked = source.checked;

    if (!isEmpty(dataPrev)) {
      checked = !some({ checked: false }, leafs);
    }
    return checked;
  }

  /**
   * Get checked for leaf node checkbox
   * @param {Object} data previous
   * @param {Object} source
   * @returns {Boolean} checked
   */
  function getLeafNodeChecked(dataPrev, source) {
    var id = source.id;
    var checked = source.checked;

    if (!isEmpty(dataPrev)) {
      var prev = find(function(prev) {
        return isEqual(prev.id, id);
      }, dataPrev);
      if (!isNil(prev)) {
        checked = prev.checked;
      }
    }
    return checked;
  }

  /**
   * onchange all
   * @param {Object} props
   * @param {Object} source
   * @param {Object} parent
   * @param {Object} input
   */
  function onChangeAll(props, source, parent, e) {
    var leafKey = props.leafKey;
    var check = e.target.checked;
    source.checked = check;
    source.indeterminate = false;
    updateStatusForDestinationGrid(props);
    updateCheckCategories(leafKey, source.categories, check);
  }

  /**
   * onchange category
   * @param {Object} props
   * @param {Object} source
   * @param {Object} parent
   * @param {Object} input
   */
  function onChangeCategory(props, source, parent, e) {
    var leafKey = props.leafKey;
    var check = e.target.checked;
    source.checked = check;
    source.indeterminate = false;
    updateStatusForDestinationGrid(props);
    updateCheckSubCategories(leafKey, source.subCategories, check);
    updateParentNode(parent, check);
  }

  /**
   * onchange sub category
   * @param {Object} props
   * @param {Object} source
   * @param {Object} parent
   * @param {Object} input
   */
  function onChangeSubCategory(props, source, parent, e) {
    var leafKey = props.leafKey;
    var check = e.target.checked;
    source.checked = check;
    source.indeterminate = false;
    updateStatusForDestinationGrid(props);
    updateCheckLeafNodes(source[leafKey], e.target.checked);
    updateParentNode(parent, check);
  }

  /**
   * onchange report
   * @param {Object} props
   * @param {Object} source
   * @param {Object} parent
   * @param {Object} input
   */
  function onChangeLeafNode(props, source, parent, e) {
    var check = e.target.checked;
    source.checked = check;
    updateStatusForDestinationGrid(props);
    updateParentNode(parent, check);
  }

  /**
   * Update status for destination grid
   */
  function updateStatusForDestinationGrid(props) {
    props.resetWorkspaceTenantsStatus();
  }

  /**
   * update check categories
   * @param {String} leaf key
   * @param {Object} categories
   * @param {Object} check
   */
  function updateCheckCategories(leafKey, categories, check) {
    forEach(function(category) {
      category.checked = check;
      category.indeterminate = false;
      updateCheckSubCategories(leafKey, category.subCategories, check);
    }, categories);
  }

  /**
   * update check sub categories
   * @param {String} leaf key
   * @param {Object} categories
   * @param {Object} check
   */
  function updateCheckSubCategories(leafKey, subCategories, check) {
    forEach(function(subCategory) {
      subCategory.checked = check;
      subCategory.indeterminate = false;
      updateCheckLeafNodes(subCategory[leafKey], check);
    }, subCategories);
  }

  /**
   * update check leaf nodes
   * @param {Object} leafs
   * @param {Object} check
   */
  function updateCheckLeafNodes(leafs, check) {
    forEach(function(leaf) {
      leaf.checked = check;
    }, leafs);
  }

  /**
   * update parent node
   * @param {Object} parent
   * @param {Object} check
   */
  function updateParentNode(parent, check) {
    if (!isNil(parent)) {
      parent.data.source.checked = check ? check : !isCheckedLastNode(parent);
      parent.data.source.indeterminate = checkIndeterminate(parent.data.getChildNodes());
      if (!isNil(parent.parent)) {
        updateParentNode(parent.parent, check);
      }
    }
  }

  var mapProps = [];
  function buildMapProps(leafKey) {
    mapProps.push({ level: 0, prop: 'categories' });
    mapProps.push({ level: 1, prop: 'subCategories,' + leafKey });
    mapProps.push({ level: 2, prop: leafKey });
  }

  /**
   * check last node
   * @param {Object} parent
   * @returns {Boolean} last node
   */
  function isCheckedLastNode(parent) {
    var props = flow(
      find(function(mapItem) {
        return isEqual(mapItem.level, parent.level);
      }),
      get('prop'),
      split(',')
    )(mapProps);

    var isLast = true;
    forEach(function(d) {
      if (!isNil(d.id) && d.checked) {
        isLast = false;
        return false;
      }
      forEach(function(r) {
        if (r.checked) {
          isLast = false;
          return false;
        }
      }, d[props[1]]);

      if (!isLast) return false;
    }, parent.data.source[props[0]]);

    return isLast;
  }

  module.exports = TreeActions;
})();



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/CopyManagement/WorkspaceDetail/SourceDestination/Tree/Izenda.TreeActions.js